import React, { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import s from "./StatementPdf.module.scss";
import AsyncAutocomplete from "components/Input/AsyncAutocomplete/AsyncAutocomplete";
import Selection from "components/Input/Selection/Selection";
import { usePayfacOptionsCache } from "utils/optionsCache/payfacOptionsCache";
import { useMerchantOptionsCache } from "utils/optionsCache/merchantOptionsCache";
import { useTranslation } from "react-i18next";
import { searchErrorHandler } from "utils/formValidation";
import { useAuth } from "utils/useAuth/useAuth";
import ButtonMAS from "ButtonMAS/ButtonMAS";

const ALL_PAYFAC_OPTION = "ALL";

const StatementPdf = ({
  queryStatementList,
  showStatementPdf,
  generateStatementPdf,
}) => {
  const PAGE_LOCALE_KEY = "page.statement.";
  const INPUT_FIELD_INIT_STATE = {
    payfacId: "",
    merchantId: "",
    statementResponse: [],
    pdfPath: "",
    year: "",
    month: "",
  };
  const ERROR_INIT_STATE = {
    payfacId: false,
    merchantId: false,
    statementResponse: false,
    pdfPath: false,
    year: false,
    month: false,
  };
  const [searchFieldInput, setSearchFieldInput] = useState({
    ...INPUT_FIELD_INIT_STATE,
  });
  const [focus, setFocus] = useState({});
  const [loading, setLoading] = useState({});
  const [btnDisable, setBtnDisable] = useState(false);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { fetchPayfacOptions } = usePayfacOptionsCache();
  const { fetchMerchantOptions } = useMerchantOptionsCache();
  const [error, setError] = useState(ERROR_INIT_STATE);
  const [payfacOptions, setPayfacOptions] = useState([]);
  const [merchantOptions, setMerchantOptions] = useState([]);
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const hasMerchantId = (merchantId) => `${merchantId || ""}`.trim() !== "";

  useEffect(() => {
    const loadPayfacOptions = async () => {
      if (!isSuperAdmin) {
        setPayfacOptions([]);
        return;
      }

      try {
        const { options } = await fetchPayfacOptions("");
        setPayfacOptions([
          {
            key: ALL_PAYFAC_OPTION,
            title: t("common.all", "All"),
            value: ALL_PAYFAC_OPTION,
          },
          ...options,
        ]);
      } catch (e) {
        console.error("Error fetching payfac list for statement form:", e);
        setPayfacOptions([]);
      }
    };

    loadPayfacOptions();
  }, [fetchPayfacOptions, isSuperAdmin, t]);

  useEffect(() => {
    const fetchMerchants = async () => {
      if (isSuperAdmin && !searchFieldInput.payfacId) {
        setMerchantOptions([]);
        return;
      }

      try {
        const { options } = await fetchMerchantOptions({
          ...(isSuperAdmin &&
            searchFieldInput.payfacId !== ALL_PAYFAC_OPTION && {
              payfacPrefix: searchFieldInput.payfacId,
            }),
        });
        setMerchantOptions(options);
      } catch (e) {
        console.error("Error fetching merchant list for statement form:", e);
        setMerchantOptions([]);
      }
    };

    fetchMerchants();
  }, [fetchMerchantOptions, isSuperAdmin, searchFieldInput.payfacId]);

  const selectedPayfac =
    payfacOptions.find((option) => option.value === searchFieldInput.payfacId) || null;

  const selectedMerchant =
    merchantOptions.find((option) => option.value === searchFieldInput.merchantId) || null;

  const textInputOnChange = (e, field, callback) => {
    const value = callback ? callback(e.target.value) : e.target.value;
    setSearchFieldInput((prev) => ({ ...prev, [field]: value }));
  };

  const textInputOnFocus = (field) => {
    setFocus((prev) => ({ ...prev, [field]: true }));
  };

  const textInputOnBlur = (field, callback) => {
    if (searchFieldInput[field] && callback) {
      const isValidValue = callback(searchFieldInput[field]);
      if (!isValidValue) {
        setError((prev) => ({ ...prev, [field]: true }));
      }
    }
    setFocus((prev) => ({ ...prev, [field]: false }));
  };

  const handlePayfacChange = (_, newValue) => {
    const nextPayfacId = newValue?.value || "";
    setError((prev) => ({ ...prev, payfacId: false, merchantId: false }));
    setSearchFieldInput((prev) => ({
      ...prev,
      payfacId: nextPayfacId,
      merchantId: "",
      statementResponse: [],
      pdfPath: "",
    }));
  };

  const handleMerchantChange = (_, newValue) => {
    const nextMerchantId = newValue?.value || "";
    setError((prev) => ({ ...prev, merchantId: false }));
    setSearchFieldInput((prev) => ({
      ...prev,
      merchantId: nextMerchantId,
      statementResponse: [],
      pdfPath: "",
    }));
  };

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const handleOnClickViewPdfBtn = async (e, key) => {
    e?.preventDefault();
    e?.stopPropagation();

    const validators = [
      isSuperAdmin && ((input) => input.payfacId === "" && { payfacId: true }),
      (input) => !hasMerchantId(input.merchantId) && { merchantId: true },
    ].filter(Boolean);

    const { errorObj, hasError } = searchErrorHandler(searchFieldInput, validators);
    setError(errorObj);
    if (hasError) return;

    setBtnLoading(key);
    setBtnDisable(true);

    try {
      const apiResponse = await queryStatementList({
        merchantId: searchFieldInput.merchantId,
      });

      if (!apiResponse) {
        throw new Error("Network Error");
      }

      const { responseCode, data } = apiResponse.data;

      if (responseCode !== "0000") {
        throw new Error("Invalid Response");
      }

      if (!data) {
        snackbar(t(PAGE_LOCALE_KEY + "viewPdf.noRecord"), "warning");
      }

      setSearchFieldInput((prev) => ({
        ...prev,
        statementResponse: data,
        pdfPath: "",
      }));
    } catch (error) {
      snackbar(t("general.message.networkError"), "error");
    } finally {
      setBtnLoading(key);
      setBtnDisable(false);
    }
  };

  const handleOnClickStatement = async (e, period, key) => {
    e.preventDefault();
    e.stopPropagation();

    setBtnLoading(key);
    setBtnDisable(true);

    try {
      const { data } = await showStatementPdf({
        merchantId: searchFieldInput.merchantId,
        period,
      });

      if (data.responseCode !== "0000") {
        throw new Error("Network Error");
      }

      if (!data.data?.url) {
        snackbar(t(PAGE_LOCALE_KEY + "viewPdf.noRecord"), "warning");
      }
      setSearchFieldInput((prev) => ({
        ...prev,
        pdfPath: data.data.url,
      }));
    } catch (error) {
      snackbar(t("general.message.networkError"), "error");
    } finally {
      setBtnLoading(key);
      setBtnDisable(false);
    }
  };

  const handleOnClickGeneratePdf = async (e, key) => {
    e.preventDefault();
    e.stopPropagation();

    const validators = [
      isSuperAdmin && ((input) => input.payfacId === "" && { payfacId: true }),
      (input) => !hasMerchantId(input.merchantId) && { merchantId: true },
      (input) => input.year === "" && { year: true },
      (input) => input.month === "" && { month: true },
    ].filter(Boolean);

    const { errorObj, hasError } = searchErrorHandler(searchFieldInput, validators);

    setError(errorObj);
    if (hasError) return;

    setBtnLoading(key);
    setBtnDisable(true);

    try {
      const apiResponse = await generateStatementPdf({
        merchantId: searchFieldInput.merchantId,
        period: `${searchFieldInput.year}-${searchFieldInput.month}`,
      });

      if (!apiResponse || !apiResponse.data) {
        throw new Error("Network Error");
      }

      // Check for 404 responseCode in the response body
      if (apiResponse.data.responseCode === "404") {
        snackbar(t(PAGE_LOCALE_KEY + "generatePdf.noMerchantsFound"), "warning");
        return;
      }

      const { responseCode, message } = apiResponse.data;
      const appendServerMessage = (baseMessage) =>
        message ? `${baseMessage}: ${message}` : baseMessage;

      switch (responseCode) {
        case "0000": {
          snackbar(t(PAGE_LOCALE_KEY + "generatePdf.success"), "success");
          break;
        }
        case "0001": {
          snackbar(appendServerMessage(t(PAGE_LOCALE_KEY + "generatePdf.fail")), "error");
          break;
        }
        case "0002": {
          snackbar(t(PAGE_LOCALE_KEY + "generatePdf.fail.missingLogo"), "error");
          break;
        }
        default: {
          snackbar(
            appendServerMessage(t(PAGE_LOCALE_KEY + "generatePdf.fail")),
            "error",
          );
          break;
        }
      }

    } catch (error) {
      // Check if the error is a 404 HTTP status or 404 responseCode
      if (error?.response?.status === 404 || error?.status === 404 || error?.response?.data?.responseCode === "404") {
        snackbar(t(PAGE_LOCALE_KEY + "generatePdf.noMerchantsFound"), "warning");
      } else {
        snackbar(t("general.message.networkError"), "error");
      }
    } finally {
      setBtnLoading(key);
      setBtnDisable(false);
    }
  };

  const setBtnLoading = (btnKey) => {
    setLoading((prev) => ({ ...prev, [btnKey]: !prev[btnKey] }));
  };

  const renderStatementTable = () => {
    let groupedData = {};
    // Group periods by year
    if (
      searchFieldInput.statementResponse &&
      searchFieldInput.statementResponse.length > 0
    ) {
      groupedData = searchFieldInput.statementResponse.reduce((acc, item) => {
        const year = item.period.split("-")[0];
        if (!acc[year]) {
          acc[year] = [];
        }
        acc[year].push(item.period);
        return acc;
      }, {});
    }
    if (Object.keys(groupedData).length === 0) {
      return null;
    }
    const sortedYears = Object.keys(groupedData).sort((a, b) => b - a);

    return (
      <div style={{ maxWidth: "500px" }}>
        <h3
          style={{ fontWeight: "500" }}
        >{`${searchFieldInput.statementResponse[0]["merchant_name"]}'s Statement`}</h3>
        {sortedYears.map((year) => (
          <div key={year}>
            <h3 style={{ fontWeight: "500" }}>{year}</h3>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
                fontWeight: "500",
              }}
            >
              {groupedData[year].map((period) => {
                const [year, month] = period.split("-");
                const formattedPeriod = `${month}/${year}`;

                return (
                  <ButtonMAS
                    key={formattedPeriod}
                    variant="bordered"
                    onClick={(e) =>
                      handleOnClickStatement(e, period, formattedPeriod)
                    }
                    disabled={btnDisable}
                    isLoading={loading[formattedPeriod]}
                  >
                    {formattedPeriod}
                  </ButtonMAS>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const generateLastNYears = (n) => {
    const currentYear = new Date().getFullYear();
    const years = [];

    for (let i = 0; i < n; i++) {
      const year = currentYear - i;
      years.push({
        key: year.toString(),
        title: year.toString(),
        value: year.toString(),
      });
    }
    return years;
  };

  const generateMonths = [
    { key: "01", title: "1", value: "01" },
    { key: "02", title: "2", value: "02" },
    { key: "03", title: "3", value: "03" },
    { key: "04", title: "4", value: "04" },
    { key: "05", title: "5", value: "05" },
    { key: "06", title: "6", value: "06" },
    { key: "07", title: "7", value: "07" },
    { key: "08", title: "8", value: "08" },
    { key: "09", title: "9", value: "09" },
    { key: "10", title: "10", value: "10" },
    { key: "11", title: "11", value: "11" },
    { key: "12", title: "12", value: "12" },
  ];

  const hasStatements = searchFieldInput.statementResponse && searchFieldInput.statementResponse.length > 0;

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        <div className={`${s.formSection} ${s.previewSection}`}>
          <div className={s.selectionRow}>
            {isSuperAdmin && (
              <div className={s.inputField}>
                <AsyncAutocomplete
                  label={t(PAGE_LOCALE_KEY + "viewPdf.payfacIdLabel")}
                  required
                  disabled={btnDisable}
                  preloadedOptions={payfacOptions}
                  useLocalFilter
                  value={selectedPayfac}
                  onChange={handlePayfacChange}
                  getOptionLabel={(option) => option.title || ""}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  placeholder={t(PAGE_LOCALE_KEY + "viewPdf.inputLabel")}
                  error={!!error.payfacId}
                />
              </div>
            )}

            <div className={s.inputField}>
              <AsyncAutocomplete
                label={t(PAGE_LOCALE_KEY + "viewPdf.merchantIdLabel")}
                required
                disabled={btnDisable || (isSuperAdmin && !searchFieldInput.payfacId)}
                preloadedOptions={merchantOptions}
                useLocalFilter
                numericExactMatch
                value={selectedMerchant}
                onChange={handleMerchantChange}
                getOptionLabel={(option) => option.title || ""}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                placeholder={
                  isSuperAdmin && !searchFieldInput.payfacId
                    ? t(PAGE_LOCALE_KEY + "viewPdf.selectPayfacFirst")
                    : t(PAGE_LOCALE_KEY + "viewPdf.merchantIdPlaceholder")
                }
                noOptionsText={
                  isSuperAdmin && searchFieldInput.payfacId && merchantOptions.length === 0
                    ? t(PAGE_LOCALE_KEY + "viewPdf.noMerchantAvailable")
                    : undefined
                }
                error={!!error.merchantId}
              />
            </div>
          </div>

          <div className={s.actionRow}>
            <ButtonMAS
              onClick={(e) => handleOnClickViewPdfBtn(e, "viewPdfBtn")}
              isLoading={!!loading?.viewPdfBtn}
              disabled={btnDisable || !!loading?.viewPdfBtn}
            >
              {t(PAGE_LOCALE_KEY + "viewPdf.buttonTitle")}
            </ButtonMAS>
          </div>

          {!hasStatements ? (
            <div className={s.description}>
              {t(PAGE_LOCALE_KEY + "viewPdf.noStatementAvaible")}
            </div>
          ) : (
            <>
              <div className={s.description}>
                {t(PAGE_LOCALE_KEY + "viewPdf.description")}
              </div>
              <div className={s.statementContent}>
                {renderStatementTable()}
                {searchFieldInput.pdfPath && (
                  <div className={s.pdfPreview}>
                    <object
                      style={{ width: "100%", height: "1000px" }}
                      data={searchFieldInput.pdfPath}
                      type="application/pdf"
                    ></object>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <hr className={s.hr} />

        <div className={s.formSection}>
          <div className={s.selectionRow}>
            <div className={s.inputField}>
              <Selection
                disabled={btnDisable}
                selectionValue={searchFieldInput.year}
                label={t(PAGE_LOCALE_KEY + "generatePdf.yearLabel")}
                options={generateLastNYears(10)}
                sxParams={{
                  selectionFocus: focus.year,
                  selectionError: error.year,
                }}
                onchangeFn={(e) => textInputOnChange(e, "year")}
                onfocusFn={() => textInputOnFocus("year")}
                onblurFn={() => textInputOnBlur("year")}
                errorMessage={
                  error.year
                    ? t(PAGE_LOCALE_KEY + "generatePdf.selectionError.year")
                    : ""
                }
              />
            </div>
            <div className={s.inputField}>
              <Selection
                selectionValue={searchFieldInput.month}
                label={t(PAGE_LOCALE_KEY + "generatePdf.monthLabel")}
                disabled={btnDisable}
                options={generateMonths}
                sxParams={{
                  selectionFocus: focus.month,
                  selectionError: error.month,
                }}
                onchangeFn={(e) => textInputOnChange(e, "month")}
                onfocusFn={() => textInputOnFocus("month")}
                onblurFn={() => textInputOnBlur("month")}
                errorMessage={
                  error.month
                    ? t(PAGE_LOCALE_KEY + "generatePdf.selectionError.month")
                    : ""
                }
              />
            </div>
          </div>

          <div className={s.actionRow}>
            <ButtonMAS
              onClick={(e) => handleOnClickGeneratePdf(e, "generatePdfBtn")}
              isLoading={!!loading?.generatePdfBtn}
              disabled={btnDisable || !!loading?.generatePdfBtn}
            >
              {t(PAGE_LOCALE_KEY + "generatePdf.buttonTitle")}
            </ButtonMAS>
          </div>

          <div className={s.description}>
            {t(PAGE_LOCALE_KEY + "generatePdf.description")}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(StatementPdf);
